import { UseFormReturn, useForm } from "react-hook-form"; // Ensure useForm is imported
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ListingFormValues,
  listingFormSchema,
} from "@/components/listings/filters/FilterSchema";
// Remove the context import as we'll pass the function

export const useFormInitialization = (
  loadFormDataFromLocalStorage: () => ListingFormValues | undefined
) => {
  const savedData = loadFormDataFromLocalStorage(); // Attempt to load data from local storage

  const form = useForm<ListingFormValues>({
    resolver: zodResolver(listingFormSchema),
    mode: "onBlur", // Validate on blur
    reValidateMode: "onChange", // Re-validate on change after first blur/submit
    defaultValues: savedData || {
      // Use savedData if available, otherwise use default values
      // Step 0: Basic Info
      propertyType: undefined, // Should be undefined to trigger Zod's required_error for min(1)
      listingType: "sale",
      price: 0, // Price can be 0 as per schema.sql, but backend validation is >=0
      currency: "LKR",
      pricePeriod: undefined, // Should be undefined to trigger Zod's required_error for min(1)

      // Step 1: Property Details
      size: undefined, // Optional
      size_unit: "sqft", // Default
      floorSizeSqFt: undefined, // Optional
      plotSizeSqft: undefined, // Optional
      rooms: undefined, // Optional
      bedrooms: undefined, // Optional
      bathrooms: undefined, // Optional
      parkings: undefined, // Optional
      floors: undefined, // Optional
      furnishing: undefined, // Optional
      status: undefined, // Optional, maps to availability_status
      advancePayment: undefined, // Optional
      minimumTerm: undefined, // Optional
      depositRequired: undefined, // Optional
      shortTermAvailable: false, // Default
      billsIncluded: false, // Default
      features: [], // Default
      amenities: [], // Default

      // Step 2: Location
      location: {
        city_id: undefined, // Should be undefined to trigger Zod's required_error for min(1)
        address: "", // Empty string to trigger Zod's minLength(5)
        latitude: undefined, // Optional
        longitude: undefined, // Optional
      },

      // Step 3: Media
      images: [], // Default

      // Step 4: Contact Details
      contactDetails: {
        // Optional object, but fields within are optional
        name: undefined,
        phone: undefined,
        email: undefined,
        whatsapp: false,
        botim: false,
        hideContactUntilRequest: false,
      },

      // Step 5: Title and Description
      title: undefined, // Should be undefined to trigger Zod's minLength(5)
      description: undefined, // Should be undefined to trigger Zod's minLength(20)
    },
  });

  return form;
};
